import { useState, useRef, useEffect } from 'react';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mobile menu on resize to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) setMobileMenuOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 glass-nav border-b border-background-200/60'
          : 'bg-background-50/80 glass-nav border-b border-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <a href="/" className="flex items-center gap-1 group flex-shrink-0">
            <span className="text-foreground-500 text-lg font-light">/</span>
            <span className="text-foreground-950 font-heading text-lg font-semibold tracking-tight">
              itaobuy<span className="text-primary-500">.run</span>
            </span>
          </a>

          {/* Desktop Center Nav */}
          <div className="hidden md:flex items-center gap-1" ref={dropdownRef}>
            <a href="/" className="px-3 py-2 text-sm text-foreground-700 hover:text-foreground-950 smooth-transition font-medium whitespace-nowrap cursor-pointer rounded-lg hover:bg-background-100">
              Home
            </a>

            {/* Category Dropdown */}
            <div className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-1.5 px-3 py-2 text-sm text-foreground-700 hover:text-foreground-950 smooth-transition font-medium whitespace-nowrap cursor-pointer rounded-lg hover:bg-background-100"
              >
                <span>Categories</span>
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[520px] bg-background-50 rounded-2xl border border-background-200/70 p-5 grid grid-cols-2 gap-x-8 gap-y-2 shadow-lg">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-background-100 smooth-transition group whitespace-nowrap cursor-pointer"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-primary-500 flex-shrink-0"></span>
                      <span className="text-sm text-foreground-800 group-hover:text-foreground-950 font-medium">
                        {cat.name}
                      </span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a href="/guide/itaobuyspreadsheet-reseller-growth" className="px-3 py-2 text-sm text-foreground-600 hover:text-foreground-950 smooth-transition whitespace-nowrap cursor-pointer rounded-lg hover:bg-background-100">
              Shopping Guide
            </a>
            <a href="#how-it-works" className="px-3 py-2 text-sm text-foreground-600 hover:text-foreground-950 smooth-transition whitespace-nowrap cursor-pointer rounded-lg hover:bg-background-100">How It Works</a>
            <a href="#features" className="px-3 py-2 text-sm text-foreground-600 hover:text-foreground-950 smooth-transition whitespace-nowrap cursor-pointer rounded-lg hover:bg-background-100">Features</a>
            <a href="#blog" className="px-3 py-2 text-sm text-foreground-600 hover:text-foreground-950 smooth-transition whitespace-nowrap cursor-pointer rounded-lg hover:bg-background-100">Guides</a>
          </div>

          {/* Desktop Right CTA */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center px-4 py-2 text-sm font-medium border border-primary-400 text-primary-700 rounded-full hover:bg-primary-50 smooth-transition whitespace-nowrap cursor-pointer"
            >
              itaobuy sheet
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold bg-foreground-950 text-background-50 rounded-full hover:bg-foreground-800 smooth-transition whitespace-nowrap cursor-pointer"
            >
              <span className="w-5 h-5 rounded-full bg-accent-500 flex items-center justify-center flex-shrink-0">
                <i className="ri-shopping-bag-line text-white text-xs"></i>
              </span>
              buy it now
              <i className="ri-arrow-right-up-line text-sm"></i>
            </a>
          </div>

          {/* Mobile Hamburger */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg text-foreground-700 hover:bg-background-100 smooth-transition cursor-pointer"
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 top-16 z-40 bg-background-50 overflow-y-auto">
          <div className="px-4 py-4 space-y-1">
            <a
              href="/"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-foreground-800 hover:bg-background-100 smooth-transition font-medium text-sm cursor-pointer"
            >
              <i className="ri-home-4-line text-lg text-primary-500"></i>
              Home
            </a>

            <a
              href="/guide/itaobuyspreadsheet-reseller-growth"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-foreground-800 hover:bg-background-100 smooth-transition font-medium text-sm cursor-pointer"
            >
              <i className="ri-book-open-line text-lg text-primary-500"></i>
              Shopping Guide
            </a>

            {/* Mobile Categories */}
            <div className="pt-2 pb-1">
              <p className="px-4 py-2 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Product Categories</p>
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-foreground-700 hover:bg-background-100 smooth-transition text-sm cursor-pointer"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-primary-400 flex-shrink-0"></span>
                  {cat.name}
                </a>
              ))}
            </div>

            <div className="border-t border-background-200 my-2"></div>

            <a
              href="#how-it-works"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-foreground-800 hover:bg-background-100 smooth-transition font-medium text-sm cursor-pointer"
            >
              <i className="ri-guide-line text-lg text-foreground-500"></i>
              How It Works
            </a>
            <a
              href="#features"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-foreground-800 hover:bg-background-100 smooth-transition font-medium text-sm cursor-pointer"
            >
              <i className="ri-star-line text-lg text-foreground-500"></i>
              Features
            </a>
            <a
              href="#blog"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-foreground-800 hover:bg-background-100 smooth-transition font-medium text-sm cursor-pointer"
            >
              <i className="ri-article-line text-lg text-foreground-500"></i>
              SEO Guides
            </a>

            <div className="border-t border-background-200 my-2"></div>

            <div className="px-4 py-2 space-y-2">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 w-full px-4 py-3 text-sm font-medium border border-primary-400 text-primary-700 rounded-full hover:bg-primary-50 smooth-transition whitespace-nowrap cursor-pointer"
              >
                itaobuy sheet
                <i className="ri-external-link-line text-xs"></i>
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 w-full px-4 py-3 text-sm font-semibold bg-foreground-950 text-background-50 rounded-full hover:bg-foreground-800 smooth-transition whitespace-nowrap cursor-pointer"
              >
                <span className="w-5 h-5 rounded-full bg-accent-500 flex items-center justify-center flex-shrink-0">
                  <i className="ri-shopping-bag-line text-white text-xs"></i>
                </span>
                buy it now
                <i className="ri-arrow-right-up-line text-sm"></i>
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}